import { i18n } from "./plugins/vuetify"
import { useSettingsStore } from "./store/index"

/**
 * resolveUserRole
 * Derives the three role flags from the raw role string returned by
 * useAuthStore.getUserRoleFromLocalStorage.
 *
 * @param {string|null} role
 * @returns {{ isBroker: boolean, isPartner: boolean, isNotPro: boolean }}
 */
export function resolveUserRole (role) {
  const isBroker  = role === 'broker'
  const isPartner = role === 'partner'
  return { isBroker, isPartner, isNotPro: !isBroker && !isPartner }
}

/**
 * ClearFX AI's own broker. It is the only broker allowed to see every broker
 * in the system; every other broker is a master in its own right and must only
 * see the partners (slave brokers) hanging under it.
 */
export const CFX_ROOT_BROKERID = 'de9fb8ddc2c83a317f854423e6b31a8dc6fde896'

/**
 * getCurrentBrokerId
 * The broker of the logged-in user. localStorage stringifies a missing value,
 * so "null"/"undefined" have to be treated as absent.
 *
 * @returns {string|null}
 */
export function getCurrentBrokerId () {
  const brokerId = localStorage.getItem('brokerId')
  if (!brokerId || brokerId === 'null' || brokerId === 'undefined') return null
  return brokerId
}

/**
 * isClearfxaiBroker
 *
 * @param {string|null} brokerId defaults to the logged-in broker
 * @returns {boolean}
 */
export function isClearfxaiBroker (brokerId = getCurrentBrokerId()) {
  return brokerId === CFX_ROOT_BROKERID
}

/**
 * scopeBrokersToCurrentBroker
 * Narrows a raw broker list (as returned by /admin/brokers) down to what the
 * logged-in broker is allowed to see: everything for ClearFX AI, and only its
 * own row plus its own partners (partmaster_id === its id) for anyone else.
 *
 * @param {Array} brokers
 * @param {string|null} brokerId defaults to the logged-in broker
 * @returns {Array}
 */
export function scopeBrokersToCurrentBroker (brokers, brokerId = getCurrentBrokerId()) {
  const list = Array.isArray(brokers) ? brokers : []
  if (isClearfxaiBroker(brokerId)) return list
  if (!brokerId) return []
  return list.filter(b => b.id === brokerId || b.partmaster_id === brokerId)
}

/**
 * scopeCompaniesToCurrentBroker
 * Same rule as scopeBrokersToCurrentBroker, one level down: a company belongs
 * to the broker in its `broker` field, so the logged-in broker only sees the
 * companies hanging under a broker it is already allowed to see (itself and
 * its own partners). ClearFX AI sees them all.
 *
 * @param {Array} companies
 * @param {Array} brokers the raw broker list, needed to know which are ours
 * @param {string|null} brokerId defaults to the logged-in broker
 * @returns {Array}
 */
export function scopeCompaniesToCurrentBroker (companies, brokers, brokerId = getCurrentBrokerId()) {
  const list = Array.isArray(companies) ? companies : []
  if (isClearfxaiBroker(brokerId)) return list
  if (!brokerId) return []
  const allowedBrokerIds = new Set(scopeBrokersToCurrentBroker(brokers, brokerId).map(b => b.id))
  return list.filter(c => allowedBrokerIds.has(c.broker))
}

/**
 * scopeUsersToCurrentBroker
 * A user is visible when the entity behind it is: either its broker or its
 * company has to be one the logged-in broker already sees. Bank users fall
 * outside that set by design — a broker that cannot open the Banks tab has no
 * business listing the people behind those banks.
 *
 * @param {Array} users
 * @param {Array} companies the raw company list
 * @param {Array} brokers the raw broker list
 * @param {string|null} brokerId defaults to the logged-in broker
 * @returns {Array}
 */
export function scopeUsersToCurrentBroker (users, companies, brokers, brokerId = getCurrentBrokerId()) {
  const list = Array.isArray(users) ? users : []
  if (isClearfxaiBroker(brokerId)) return list
  if (!brokerId) return []
  const allowedBrokerIds = new Set(scopeBrokersToCurrentBroker(brokers, brokerId).map(b => b.id))
  const allowedCompanyIds = new Set(scopeCompaniesToCurrentBroker(companies, brokers, brokerId).map(c => c.id))
  return list.filter(u => allowedBrokerIds.has(u.broker) || allowedCompanyIds.has(u.company))
}


// PADEND METHODS

// padEnd2
// (5) returns "5.00"
// (5.4) returns "5.40"
// (5.456) returns "5.45"
export function padEnd2 (value) {
  if (!isNaN(value)) {
    const decimalPlaces = 2
    const padEndValue = "0"
    value = value.toString()
    let result = value.split(".")
    if (result[1] && result[1].length > decimalPlaces) {
      result[1] = result[1].substring(0, decimalPlaces)
      return result[0].concat(".").concat(result[1])
    }
    if (result[1]) {
      result[1] = result[1].padEnd(decimalPlaces, padEndValue)
      return result[0].concat(".").concat(result[1])
    }
    const decimals = "".padEnd(decimalPlaces, padEndValue)
    return value.concat(".").concat(decimals)
  }
  return value
}

export default function convertToCNPJ(cnpj) {
      if(!cnpj) return;
      const raw = cnpj.replace(/[^0-9]/g, "");
      if (raw === "") {
      cnpj = "";
        return;
      }
      let formatted = "";
      if (raw.length > 0) {
        formatted = raw.slice(0, 2);
      }
      if (raw.length > 2) {
        formatted += "." + raw.slice(2, 5);
      }
      if (raw.length > 5) {
        formatted += "." + raw.slice(5, 8);
      }
      if (raw.length > 8) {
        formatted += "/" + raw.slice(8, 12);
      }
      if (raw.length > 12) {
        formatted += "-" + raw.slice(12, 14);
      }
      return formatted;
    }
// padEnd4
// (5) returns "5.0000"
// (5.4) returns "5.4000"
// (5.456) returns "5.4560"
export function padEnd4 (value) {
  if (isNaN(value)) {
    return value
  }
  const decimalPlaces = 4
  const padEndValue = "0"
  value = value.toString()
  let result = value.split(".")
  if (result[1] && result[1].length > decimalPlaces) {
    result[1] = result[1].substring(0, decimalPlaces)
    return result[0].concat(".").concat(result[1])
  }
  if (result[1]) {
    result[1] = result[1].padEnd(decimalPlaces, padEndValue)
    return result[0].concat(".").concat(result[1])
  }
  const decimals = "".padEnd(decimalPlaces, padEndValue)
  return value.concat(".").concat(decimals)
}

// padEndJpy
// (5) returns "5.000000"
// (5.4) returns "5.400000"
// (5.456) returns "5.456000"
export function padEndJpy (value) {
  if (!isNaN(value)) {
    const decimalPlaces = 6
    const padEndValue = "0"
    value = value.toString()
    let result = value.split(".")
    if (result[1]) {
      result[1] = result[1].padEnd(decimalPlaces, padEndValue)
      return result[0].concat(".").concat(result[1])
    }
    const decimals = "".padEnd(decimalPlaces, padEndValue)
    return value.concat(".").concat(decimals)
  }
  return value
}

// PRICE AND BIPS METHODS

// getPriceAndBips
// (5.1234) returns { price: "5.12", bips: "34" }
// (5.1) returns { price: "5.10", bips: "00" } 
export function getPriceAndBips(price) {
  price = padEnd4(price);
  const priceSlited = price.split(".");
  const integer = priceSlited[0];
  let decimals = priceSlited[1];
  let sliceParameter = -2;
  const bips = decimals.slice(sliceParameter);
  decimals = decimals.slice(0, sliceParameter);
  price = `${integer}.${decimals}`;
  return { price: price, bips: bips };
}

// getPriceAndBips2
// (5.1234) returns { price: "5.", bips: "12" }
// (5.1) returns { price: "5.", bips: "10" } 
export function getPriceAndBips2(price) {
  price = padEnd2(price);
  const priceSlited = price.split(".");
  const integer = priceSlited[0];
  let decimals = priceSlited[1];
  let sliceParameter = -2;
  const bips = decimals.slice(sliceParameter);
  decimals = decimals.slice(0, sliceParameter);
  price = `${integer}.${decimals}`;
  return { price: price, bips: bips };
}

// getPriceAndBipsJPY
// (3.456789) returns { price: "3.45", bips: "6789" }
// (123.456789) returns { price: "123.45", bips: "6789" }
export function getPriceAndBipsJPY(price) {
  price = padEndJpy(price)
  const priceSlited = price.split(".")
  const integer = priceSlited[0]
  let decimals = priceSlited[1]
  const sliceParameter = -4
  const bips = decimals.slice(sliceParameter)
  decimals = decimals.slice(0, sliceParameter)
  price = `${integer}.${decimals}`
  return { price: price, bips: bips }
}

// FORMAT PRICE METHODS

// formatPrice
// (1234.56) returns "1,234.56"
// (1234.56789) return "1,234.56789"
// (1234567) returns "1,234,567.00"
export function formatPrice (price) {
  if (price === null) return ''
  if (price === '') return ''
  if (isNaN(price)) return price
  const n = Number(price)
  if (!Number.isFinite(n)) return String(price)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// formatBRLPretty
// (1234.56) returns "1.234,56"
// (1234.56789) return "1.234,57"
// (1234567) returns "1.234.567,00"
export function formatBRLPretty (price) {
  const ccy = "BRL"
  const intlNumberFormat = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: ccy,
    currencyDisplay: "code"
	})
  price = intlNumberFormat.format(price)
  price = price.replace(ccy, "").trim()
  return price
}

// formatUSDPretty
// (1234.56) returns "1,234.56"
// (1234.56789) return "1,234.57"
// (1234567) returns "1,234,567.00"
export function formatUSDPretty (price) {
  if (!price) {
    return price
  }
  const ccy = "USD"
  const intlNumberFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: ccy,
    currencyDisplay: "code"
	})
  price = intlNumberFormat.format(price)
  price = price.replace(ccy, "").trim()
  return price
}

// TIME & DATE METHODS

// getCurrentTime
// pega a hora atual do sistema e exibe como HH:MM:SS
export function getCurrentTime() {
  const now = new Date()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  let formattedTime = `${hours}:${minutes}:${seconds}`
  return formattedTime
}

// getOffsetDate
// retorna a data de hoje ou deslocada em dias.
// Se forAvgPtax = false → formato YYYY-MM-DD (ISO-like).
// Se forAvgPtax = true → formato DD/MM/YYYY (estilo brasileiro).
export function getOffsetDate(offsetDays = 0, forAvgPtax = false) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

    if (forAvgPtax) {
      return `${day}/${month}/${year}`
    }

  let formattedDate = `${year}-${month}-${day}`
  return formattedDate
}

// getCurrentDateAndTime
// returna getOffsetDate no padrão YYYY-MM-DD e getCurrentTime no padrão HH:MM:SS
export function getCurrentDateAndTime(offsetDays = 0) {
  return `${getOffsetDate(offsetDays)} ${getCurrentTime()}`
}


// getTimeZone
// retorna a timezone selecionada pelo usuário
export function getTimeZone() {
  const { timezone } = useSettingsStore()
  return timezone === 'BRT' ? 'America/Sao_Paulo' : 'UTC'
}

// getDateParts
// pode receber como argumento números (timestamps), strings de data, objetos Date, arrays/objetos coercentes para string ou nada (data atual)

// Se usar sem argumento, retorna as partes da hora atual do sistema

// NÚMERO é lido como timestamp em milissegundos desde 1 Jan 1970 (epoch).
// (0) → 1970-01-01

// STRING sem incluir time:
// ("2025-08-27T14:35:00Z") →
// { year: "2025", month: "08", day: "27", hour: undefined, minute: undefined }

// STRING e incluir time:
// ("2025-08-27T14:35:00Z", true) →
// { year: "2025", month: "08", day: "27", hour: "11", minute: "35" }

// OBJECT coercente
// ({ toString: () => "2025-08-27" }) é equivalente a usar a STRING "2025-08-27"

// OBJECT Date é aceito e simplesmente copiado

// ARRAY-like convertido para STRING pode funcionar, mas não é seguro:
// new Date([2025, 7, 27]) vira new Date("2025,7,27") → interpretado como string pelo parser, pode variar por navegador.

export function getDateParts(value, includeTime = false, locale = 'en-GB') {
  const timeZone = getTimeZone()
  const date = new Date(value)

  const options = {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })
  }

  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date)
  const get = type => parts.find(p => p.type === type)?.value || ''

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: includeTime ? get('hour') : undefined,
    minute: includeTime ? get('minute') : undefined
  }
}

// formatTime
// recebe timestamp em segundos (Unix), e retorna HH:MM AM ou PM
export function formatTime(timestamp) {
  const date = new Date(timestamp)
  const timeZone = getTimeZone()

  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone
  }

  return new Intl.DateTimeFormat(i18n.locale, options).format(date)
}

// formatDateYMD
// recebe timestamp em segundos (Unix) e retorna data formatada como yyyy-mm-dd
export function formatDateYMD(timestamp) {
  if (timestamp === undefined || timestamp === null) return '-'
  const date = new Date(Number(timestamp) * 1000)
  if (isNaN(date.getTime())) return '-'

  const timeZone = getTimeZone()
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const y = parts.find(p => p.type === 'year')?.value
  const m = parts.find(p => p.type === 'month')?.value
  const d = parts.find(p => p.type === 'day')?.value

  return `${y}-${m}-${d}`
}

// formatDateYM
// recebe timestamp em segundos (Unix) e retorna data formatada como yyyy-mm
export function formatDateYM(timestamp) {
  if (timestamp === undefined || timestamp === null) return '–'
  const date = new Date(Number(timestamp) * 1000)
  if (isNaN(date.getTime())) return '–'

  const timeZone = getTimeZone()
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(date)

  const y = parts.find(p => p.type === 'year')?.value
  const m = parts.find(p => p.type === 'month')?.value

  return `${y}-${m}`
}

// formatShortDate
// recebe timestamp em segundos (Unix) e retorna data formatada com dia, mes curto e ano de acordo com o locale do sistema
export function formatShortDate(timestamp) {
  if (timestamp === undefined || timestamp === null) return '–'
  const date = new Date(Number(timestamp) * 1000)
  if (isNaN(date.getTime())) return '–'

  const timeZone = getTimeZone()
  return new Intl.DateTimeFormat(i18n.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone
  }).format(date)
}

// formatDate
// recebe timestamp em segundos (Unix) e retorna data no formato yyyy-mm-dd
export function formatDate(timestamp) {
  return formatDateYMD(timestamp)
}

// formatLongDateFromTimestamp
// recebe um timestamp em segundos (Unix) e retorna a data longa no padrão en-US:
// (1712282386) → "Wednesday, August 27, 2025"
export function formatLongDateFromTimestamp (timestamp) {
  if (!timestamp) return '–'
  const date = new Date(timestamp * 1000)

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

// formatDateAndTimeFromString
// recebe uma string e retorna a data no padrão en-US, com dia da semana e mês abreviados:
// ("2025-08-27") → "Wed, Aug 27, 2025"
// ("2025-08-27 15:30:00") → "Wed, Aug 27, 2025 at 12:30:00 PM"
// ("2025-08-27T15:30:00Z") → "Wed, Aug 27, 2025 at 12:30:00 PM"
export function formatDateAndTimeFromString(dateInput) {
  const str = String(dateInput || '').trim()
  if (!str) return 'Invalid Date'

  // detect time in the input (supports "YYYY-MM-DD HH:mm(:ss)?" or "YYYY-MM-DDTHH:mm(:ss)?")
  const hasTime = /[ T]\d{2}:\d{2}(:\d{2})?/.test(str)

  // normalize: replace space with 'T' and ensure seconds if missing
  let normalized = str
  if (hasTime) {
    normalized = normalized.replace(/\s+/, 'T')
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) {
      normalized += ':00'
    }
  }

  // build Date: for date-only, anchor at midnight
  const date = hasTime
    ? new Date(normalized)
    : new Date(`${normalized}T00:00:00`)

  if (isNaN(date)) return 'Invalid Date'

  const timeZone = getTimeZone()
  const locale = 'en-US'

  const dateOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone
  }

  if (!hasTime) {
    return date.toLocaleDateString(locale, dateOptions)
  }

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone
  }

  const datePart = date.toLocaleDateString(locale, dateOptions)
  const timePart = date.toLocaleTimeString(locale, timeOptions)

  return `${datePart} at ${timePart}`
}

// formatTimeAndDate
// recebe um timestamp em segundos (Unix) e retorna a data e hora formatadas de acordo com o locale, com ou sem segundos:
// (1712282386) 
//  → en-US: "Wed, Apr 04, 2024 at 01:19:46 PM"
//  → pt-BR: "qua., 04 de abr. de 2024 às 13:19:46"
// (1712282386, false)
//  → en-US: "Wed, Apr 04, 2024 at 01:19 PM"
//  → pt-BR: "qua., 04 de abr. de 2024 às 13:19"
export function formatTimeAndDate(epoch, showSeconds = true) {
  const toMs = v => {
    if (v == null) return Date.now()
    const n = Number(v)
    if (!isFinite(n)) return Date.now()
    return n > 1e12 ? n : Math.round(n * 1000)
  }

  const date = new Date(toMs(epoch))
  const timeZone = getTimeZone()
  const locale = i18n.locale

  const dateOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone
  }

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone
  }

  if (showSeconds) timeOptions.second = '2-digit'

  const formattedDate = new Intl.DateTimeFormat(locale, dateOptions).format(date)
  const formattedTime = new Intl.DateTimeFormat(locale, timeOptions).format(date)

  return `${formattedDate} ${i18n.t('extras.at')} ${formattedTime}`
}


export function parseTimestamp(t) {
  if (!t) return 0

  // format: 20260318T145200
  return new Date(
    t.slice(0, 4),
    t.slice(4, 6) - 1,
    t.slice(6, 8),
    t.slice(9, 11),
    t.slice(11, 13)
  ).getTime()
}

// formatFromCustomString
// recebe diferentes formatos de STRING, transforma em ISO, considera o locale, e retorna hora, data ou ambos:
// ("20250519T133800", "time") → "1:38 PM" (em en-US)
// ("20250519T133800", "date") → "May 19, 2025" (en-US)
// ("20250519T133800") → "May 19, 2025, 1:38 PM" (en-US)

export function formatFromCustomString(dateString, formatType = 'full') {
  const { timezone } = useSettingsStore()
  const locale = i18n.locale


  let year, month, day, hour = '00', minute = '00'

  if (/^\d{8}T\d{6}$/.test(dateString)) {
    // Format: 20250519T133800
    year = dateString.slice(0, 4)
    month = dateString.slice(4, 6)
    day = dateString.slice(6, 8)
    hour = dateString.slice(9, 11)
    minute = dateString.slice(11, 13)
  } else if (dateString.includes('-')) {
    // Format: 20250515-1430
    year = dateString.slice(0, 4)
    month = dateString.slice(4, 6)
    day = dateString.slice(6, 8)
    hour = dateString.slice(9, 11)
    minute = dateString.slice(11, 13)
  } else if (dateString.includes('/')) {
    const parts = dateString.split('/')
    if (locale === 'pt') {
      day = parts[0]; month = parts[1]; year = parts[2]
    } else {
      month = parts[0]; day = parts[1]; year = parts[2]
    }
  }

  const iso = `${year}-${month}-${day}T${hour}:${minute}:00Z`
  const date = new Date(iso)

  if (isNaN(date)) return 'Invalid Date'

  const timeZone = timezone === 'BRT' ? 'America/Sao_Paulo' : 'UTC'

  // just 'time'
  if (formatType === 'time') {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone
    }).format(date)
  }

  // just 'date'
  if (formatType === 'date') {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone
    }).format(date)
  }

 if(formatType==="tablet")
  {
  //tablet
    return  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year:"numeric",
    timeZone
  }).format(date)

  }

    if(formatType==="mobile")
  {
  //mobile
    return  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone
  }).format(date)

  }

/**news */
   if(formatType==="news")
  {
     return dataFormatter(locale, date)
  }

 const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone
  }).format(date)

    if(formatType==="smallScreen")
  {
  //small desktop screen
  const formattedDate =  new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year:"2-digit",
    timeZone
  }).format(date)

    return `${formattedDate}, ${formattedTime}`

  }


  // full
  const formattedDate = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone
  }).format(date)

 

  return `${formattedDate}, ${formattedTime}`
}
export function dataFormatter(locale = i18n.locale, date) {
  const { timezone } = useSettingsStore()
  const timeZone = timezone === 'BRT' ? 'America/Sao_Paulo' : 'UTC'

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date)

  const day = parts.find(p => p.type === 'day')?.value
  const month = parts.find(p => p.type === 'month')?.value
  const hour = parts.find(p => p.type === 'hour')?.value
  const minute = parts.find(p => p.type === 'minute')?.value

  return `${day}/${month} (${hour}:${minute})`
}
export function parseCustomDate(value) {
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(9, 11));
  const minute = Number(value.slice(11, 13));
  const second = Number(value.slice(13, 15));

  return new Date(year, month, day, hour, minute, second);
}

export function formatAlphaVantage(txt)
{
  const year = txt.slice(0, 4);
  const month = txt.slice(4, 6);
  const day = txt.slice(6, 8);
  const hour = txt.slice(9, 11);
  const min = txt.slice(11, 13);
  const sec = txt.slice(13, 15);

  return new Date(`${year}-${month}-${day}T${hour}:${min}:${sec}`);
}


  export function normalizeTopic(topic) {
      if(topic.includes('Health') || topic.includes('Life Sciences') ||topic.includes('Energy') || topic.includes('IPO') ||topic.includes('Real State')  )
      {
        return null;
      }
      if (topic.includes("Monetary"))
        return "Monetary Policy";
      if (
        topic.includes("Economy") ||
        topic.includes("Manufacturing") ||
        topic.includes("Retail")
      )
        return "Economy";
      if (topic.includes("Markets") && topic.includes("Financial"))
        return "Markets";

      if (topic.includes("Finance") || topic.includes("Estate"))
        return "Finance";

      if (topic.includes("Earnings") || topic.includes("Mergers"))
        return "Corporate";

      if (
        topic.includes("Offering") ||
        topic.includes("Listing")
      )
        return "Capital Markets";

      if (topic.includes("Blockchain") || topic.includes("Technology"))
        return "Technology";

      return "Other";
    }

export function formatText(value)
  {
    if (!value) return '-';
    return value.length > 20 ? `${value.slice(0,7)}...` : value
  }

    export function nomalizeFileTitle(file)
    {
       const normalizeFileName = (s) =>
          String(s ?? "") 
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""); //replacing diacritics)

        const normalizedName = normalizeFileName(file.name)

         const newFile = new File (
            [file],
            normalizedName,
            {type: file.type}
        )
        return newFile

    }
export function parseStorageValue(value) {
  if (!value) return ''

  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'string' ? parsed : ''
  } catch {
    return String(value || '').trim()
  }
}

export function formatMetaData({
  brokerName,
  brokerCnpj,
  responsibleEmail,
  brokerProfile,
  companyName,
  companyCnpj,
  operationType,
  volume_brl,
  alias,
  operationTypes = {}
}) {
  let brokerNameFromProfile = ''
  let userCnpj = ''

  const userInfo = localStorage.getItem('userInfo')

  if (brokerProfile) {
    try {
      brokerNameFromProfile = String(JSON.parse(brokerProfile)?.name || '').trim()
    } catch {
      brokerNameFromProfile = ''
    }
  }

  if (userInfo) {
    try {
      userCnpj = String(JSON.parse(userInfo)?.cnpj || '').trim()
    } catch {
      userCnpj = ''
    }
  }

  const normalizedBrokerName =
    String(brokerName || '').trim() ||
    brokerNameFromProfile

  const normalizedBrokerCnpj =
    parseStorageValue(brokerCnpj) ||
    parseStorageValue(localStorage.getItem('brokerCnpj')) ||
    userCnpj

  const userName = parseStorageValue(localStorage.getItem('userName'))

  const operationPurposeLabel = String(
    operationTypes?.[operationType] || operationType || ''
  ).trim()

  return {
    broker_name: normalizedBrokerName,
    broker_cnpj: normalizedBrokerCnpj,
    responsible_email: String(responsibleEmail || '').trim(),
    user_name: userName,
    company_name: companyName,
    company_cnpj: companyCnpj || '',
    operation_label: String(alias || '').trim(),
    operation_purpose: operationPurposeLabel,
    volume_brl: volume_brl
  }
}

export function  blockNonNumericUtil(e){
      const allowed=['Backspace','Delete', 'ArrowLeft','ArrowRight', 'Escape'];
      if(allowed.includes(e.key)) return;
      if (!/^\d$/.test(e.key)) {
          e.preventDefault()
        }
}

export function quoteFormatting(v){
  const regex = /^[0-9]?\.?[0-9]{0,4}$/;
    const raw = v.replace(/[^0-9]/g, "")
    if (raw === "")
    {
      return
    }
    const digits = raw.slice(0,5)

    if(digits.length === 1)
    {
      return `${digits}`
    }
    else
    {
      const whole = digits[0]
      const decimals = digits.slice(1)
      return `${whole}.${decimals}`

    }
}

// Convert binary-byte scale (1024-based) to a human-expected MB number (expects 1000-based).
// e.g. 8 * 1024 * 1024 = 8388608 bytes -> display as 8 MB
// (would be 8.3 MB if naively dividing by 1000000)
export function bytesToDisplayMB(bytes) {
  if (!bytes || typeof bytes !== 'number' || bytes <= 0) return 0
  // Rounded down to a tenth, so a value just under 8 MB reads 7.9, not 8.
  return Math.floor(bytes / 1024 / 1024 * 10) / 10
}

export function formatMaturityDate(date) {
  if (!date) return "-";
  const d = String(date);
  const year = d.slice(0, 4);
  const month = d.slice(4, 6);
  const day = d.slice(6, 8);
  return `${day}/${month}/${year}`;
}

//reconciliaiton
export function normalizeReconciliationPayload(rfqToManualPrice, quote) {
  const source = rfqToManualPrice || {};

  // Only rfq_id, update_rate_client, and user_email are sent to the backend -
  // nothing else from the source item/quote should leak into the request.
  return {
    rfq_id: source.rfq_id || source.rfqId,
    rfq_px: Number(quote),
    user_email: JSON.parse(localStorage.getItem("brokerUserInfo") || "null")?.email || localStorage.getItem("userEmail") || undefined,
  };
}

// Matches sbin/orderlog_api.py + libdborderlog.py's orderlog_reconcil_px_update:
// 404 = RFQ not found, 409 = already has rfq_reconcil_sign, 400 "must be a
// number" = bad rfq_px, 400 "DEAL status" = RFQ not dealt yet, 400 "email" =
// missing user_email.
export function classifyReconcileError(error) {
  const status = error?.response?.status
  const detail = error?.response?.data?.detail || error?.response?.data?.message || ''

  if (status === 404) return 'reconcileErrorNotFound'
  if (status === 409) return 'reconcileErrorAlreadyDone'
  if (status === 400 && /number/i.test(detail)) return 'reconcileErrorInvalidPrice'
  if (status === 400 && /email/i.test(detail)) return 'reconcileErrorEmailRequired'
  if (status === 400 && /deal status/i.test(detail)) return 'reconcileErrorNotDeal'
  return 'reconcileErrorGeneric'
}


export async function submitReconcile(executionStore, data) {
  const rfqId = String(data?.rfq_id || '').trim()
  if (!rfqId) return { ok: false, key: 'reconcileErrorNotFound' }

  const userEmail = data?.user_email
  if (!userEmail) return { ok: false, key: 'reconcileErrorEmailRequired' }

  const rate = Number(data?.rfq_px)
  const isValidRate = Number.isFinite(rate) && rate >= 1 && rate <= 10 &&
    /^\d+(\.\d{1,4})?$/.test(String(data?.rfq_px))
  if (!isValidRate) return { ok: false, key: 'reconcileErrorInvalidPrice' }

  try {
    await executionStore.updateRfqReconcilePrice(rfqId, { rfq_px: rate, user_email: userEmail })
    return { ok: true }
  } catch (error) {
    return { ok: false, key: classifyReconcileError(error) }
  }
}

/**
 * phoneError
 * Is what somebody typed worth sending as a phone number?
 *
 * Not a normalizer -- the platform owns that (sbin/libutil.py) and echoes back
 * what it stored. This only disables the save button and explains an obviously
 * bad value without a round trip, so it is deliberately no stricter than the
 * platform, and its refusal codes are the same words.
 *
 * @param {string} raw
 * @returns {string} a refusal code ("letters", "missing_country_code", ...), or
 *          "" when the value is worth sending.
 */
export function phoneError(raw) {
  const text = String(raw == null ? "" : raw).trim();
  if (!text) return "empty";
  if (/[a-zA-Z]/.test(text)) return "letters";
  // The country code is stated, never inferred: 11 digits is both a Brazilian
  // mobile with DDD and a US number with its country code.
  if (!text.startsWith("+")) return "missing_country_code";

  const digits = text.replace(/\D/g, "");
  if (!digits) return "no_digits";
  // E.164 caps a full number at 15 digits; the floor is a sanity bound.
  if (digits.length < 8 || digits.length > 15) return "bad_length";
  return "";
}

/**
 * Bank operational mode constants and timeouts.
 */
export const BANK_MODE_AUTO = 'bankauto'
export const BANK_MODE_MANUAL = 'bankmanual'
export const BANK_MODE_OVERRIDE = 'brkoverride'

export const RFQ_TIMEOUT_BANK_MANUAL_MS = 180000    // 3 minutes
export const RFQ_TIMEOUT_BANK_OVERWRITE_MS = 180000 // 3 minutes

/**
 * Resolves the operational mode of a bank ('bankauto', 'bankmanual', 'brkoverride')
 * @param {Object} bank
 * @returns {string}
 */
export function getBankMode (bank) {
  if (!bank) return BANK_MODE_AUTO
  return bank.isOverride || bank.overrideMode || bank.is_override || BANK_MODE_AUTO
}

/**
 * Checks if a bank or any bank in a list has Overwrite mode ('brkoverride')
 * @param {Array|Object} banks
 * @returns {boolean}
 */
export function hasOverwriteBank (banks) {
  if (!banks) return false
  const list = Array.isArray(banks) ? banks : [banks]
  return list.some(b => getBankMode(b) === BANK_MODE_OVERRIDE)
}

/**
 * Checks if a bank or any bank in a list has BankManual mode ('bankmanual')
 * @param {Array|Object} banks
 * @returns {boolean}
 */
export function hasManualBank (banks) {
  if (!banks) return false
  const list = Array.isArray(banks) ? banks : [banks]
  return list.some(b => getBankMode(b) === BANK_MODE_MANUAL)
}

